import { clsx } from 'clsx';

type Variant = 'green' | 'red' | 'yellow' | 'blue' | 'gray' | 'purple' | 'teal';

const variantClasses: Record<Variant, string> = {
  green: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  red: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  yellow: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  blue: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  gray: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  purple: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  teal: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400',
};

interface Props {
  variant?: Variant;
  children: React.ReactNode;
  className?: string;
  dot?: boolean;
}

export function Badge({ variant = 'gray', children, className, dot }: Props) {
  return (
    <span className={clsx(
      'inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium',
      variantClasses[variant],
      className
    )}>
      {dot && (
        <span className={clsx('w-1.5 h-1.5 rounded-full mr-1', {
          'bg-green-500': variant === 'green',
          'bg-red-500': variant === 'red',
          'bg-yellow-500': variant === 'yellow',
          'bg-blue-500': variant === 'blue',
          'bg-gray-500': variant === 'gray',
          'bg-purple-500': variant === 'purple',
          'bg-teal-500': variant === 'teal',
        })} />
      )}
      {children}
    </span>
  );
}

const statusDotColors: Record<string, string> = {
  connected: '#22c55e',
  active: '#22c55e',
  disconnected: '#ef4444',
  error: '#ef4444',
  registered: '#eab308',
  pending: '#9ca3af',
  inactive: '#9ca3af',
};

export function StatusBadge({ status }: { status: string }) {
  const label = status.charAt(0).toUpperCase() + status.slice(1);

  if (status === 'connected' || status === 'active') {
    return (
      <span title={label} className="inline-flex items-center justify-center rounded-full bg-green-500" style={{ width: 15, height: 15 }}>
        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </span>
    );
  }

  if (status === 'disconnected' || status === 'error') {
    return (
      <span title={label} className="inline-flex items-center justify-center rounded-full bg-red-800" style={{ width: 15, height: 15 }}>
        <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </span>
    );
  }

  const color = statusDotColors[status] || '#9ca3af';
  return (
    <span
      className="inline-block w-2.5 h-2.5 rounded-full"
      style={{ backgroundColor: color }}
      title={label}
    />
  );
}
